import { CheckCheck, RotateCcw, Search } from 'lucide-react';
import { useEffect, useEffectEvent, useRef } from 'react';
import { Button } from '@ooh/ui/components/button';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { FilterPanel } from '@ooh/ui/components/filter-panel';
import type { FilterField } from '@ooh/ui/components/filter-panel';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ooh/ui/components/select';
import { ClientOfferReviewPageSizeControl } from './client-offer-review-page-size-control';
import type {
  ClientOfferReviewFilterKey,
  ClientOfferReviewFilterPanelLabels,
  ClientOfferReviewGranularity,
  ClientOfferReviewPageSize,
  ClientOfferReviewPhotoMode,
  ClientOfferReviewSortKey,
  ClientOfferReviewToolbarLabels,
} from './client-offer-review-types';

/** Sticky search + filter tabs + photo/sort/granularity + select/clear all + shown label. */
export function ClientOfferReviewToolbar({
  query,
  onQuery,
  filter,
  onFilter,
  counts,
  photoMode,
  onPhotoMode,
  sortKey,
  onSortKey,
  granularity,
  onGranularity,
  pageSize,
  onPageSize,
  readOnly,
  roHint,
  onSelectAll,
  onClearAll,
  shownLabel,
  labels,
  filterFields,
  panelFilters,
  onPanelFiltersChange,
  filterPanelLabels,
}: {
  query: string;
  onQuery: (v: string) => void;
  filter: ClientOfferReviewFilterKey;
  onFilter: (key: ClientOfferReviewFilterKey) => void;
  counts: Record<ClientOfferReviewFilterKey, number>;
  photoMode: ClientOfferReviewPhotoMode;
  onPhotoMode: (m: ClientOfferReviewPhotoMode) => void;
  sortKey: ClientOfferReviewSortKey;
  onSortKey: (k: ClientOfferReviewSortKey) => void;
  granularity: ClientOfferReviewGranularity;
  onGranularity: (g: ClientOfferReviewGranularity) => void;
  /** Current "results per page" size — changes how many rows each page holds (see `onPageSize`). */
  pageSize: ClientOfferReviewPageSize;
  onPageSize: (size: ClientOfferReviewPageSize) => void;
  readOnly: boolean;
  roHint: string;
  onSelectAll: () => void;
  onClearAll: () => void;
  shownLabel: string;
  labels: ClientOfferReviewToolbarLabels;
  /** FilterPanel field config, built from the real unit dataset (see `buildClientOfferReviewFilterFields`). */
  filterFields: FilterField[];
  /** FilterPanel's controlled value — one string[] per field id. */
  panelFilters: Record<string, string[]>;
  onPanelFiltersChange: (value: Record<string, string[]>) => void;
  filterPanelLabels: ClientOfferReviewFilterPanelLabels;
}) {
  const filterTabs: { key: ClientOfferReviewFilterKey; label: string }[] = [
    { key: 'ALL', label: labels.filterAll },
    { key: 'ACCEPTED', label: labels.filterAccepted },
    { key: 'REJECTED', label: labels.filterRejected },
    { key: 'PENDING', label: labels.filterPending },
  ];

  const searchInputRef = useRef<HTMLInputElement>(null);
  // Defensive native listener (CU-86cb5z48m): some clearing paths set the
  // search input's DOM value through a route that also updates React's
  // internal value tracker before dispatching a bare `input` event, so
  // React's own synthetic onChange sees no diff and never fires — the field
  // renders empty but the filtered unit list stays stuck until reload. A
  // plain DOM listener isn't gated by that tracker diff, so it always
  // resyncs `query` to whatever the input actually displays.
  const onNativeSearchInput = useEffectEvent((value: string) => {
    if (value !== query) onQuery(value);
  });
  useEffect(() => {
    const el = searchInputRef.current;
    if (!el) return;
    function handleInput(event: Event) {
      onNativeSearchInput((event.target as HTMLInputElement).value);
    }
    el.addEventListener('input', handleInput);
    return () => el.removeEventListener('input', handleInput);
  }, []);

  return (
    <div
      data-co-toolbar
      className="sticky top-0 z-30 mt-3.5 flex shrink-0 flex-col gap-2.5 border-b border-border bg-background/96 px-6 pb-2.5 pt-2.5 backdrop-blur-sm"
    >
      {/*
       * Filters render ABOVE the search/tabs/display-controls row on
       * purpose — this row sits directly above the list/map split below, so
       * the controls that decide WHICH units are shown (filters) stay
       * furthest from the list, and the controls that decide HOW the
       * (already-filtered) units are displayed (photo mode/sort/granularity)
       * sit immediately above what they affect.
       */}
      <FilterPanel
        fields={filterFields}
        value={panelFilters}
        onChange={onPanelFiltersChange}
        labels={filterPanelLabels.panel}
        rangeLabels={filterPanelLabels.range}
      />

      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative w-48 shrink">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder={labels.searchPlaceholder}
            className="h-9 rounded-lg pl-8 text-xs"
            data-testid="offer-review-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filterTabs.map((t) => {
            const active = filter === t.key;
            return (
              <Button
                key={t.key}
                type="button"
                variant="outline"
                onClick={() => onFilter(t.key)}
                data-testid={`filter-tab-${t.key}`}
                className={
                  active
                    ? 'inline-flex h-8 items-center gap-1.5 rounded-full border-primary bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'inline-flex h-8 items-center gap-1.5 rounded-full border-border bg-background px-3 text-xs font-medium text-foreground'
                }
              >
                {t.label}
                <span
                  className={
                    active
                      ? 'rounded-full bg-primary-foreground/20 px-1.5 text-2xs font-semibold tabular-nums text-primary-foreground'
                      : 'rounded-full bg-muted px-1.5 text-2xs font-semibold tabular-nums text-muted-foreground'
                  }
                >
                  {counts[t.key]}
                </span>
              </Button>
            );
          })}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/*
           * Each select gets a short visible caption via the shared `Label`
           * component (not a plain `<span>`) — `htmlFor`/`id` wires it to its
           * `SelectTrigger` so it's a real accessible label (clicking it
           * focuses the trigger), matching the DS convention every other
           * form caption in the app uses. The trigger's own selected-value
           * text ("Małe", "Połówki miesiąca") reads fine once you know what
           * the control does, but has no self-describing context on its own
           * (unlike the aria-label, which only screen readers get).
           */}
          <div className="flex items-center gap-1.5">
            <Label htmlFor="co-photo-mode-select" className="whitespace-nowrap text-xs">
              {labels.photoModeLabel}
            </Label>
            <Select
              value={photoMode}
              onValueChange={(v) => onPhotoMode(v as ClientOfferReviewPhotoMode)}
            >
              <SelectTrigger
                id="co-photo-mode-select"
                className="h-9 w-28 rounded-lg text-xs"
                aria-label={labels.photoModeAriaLabel}
                title={labels.photoModeAriaLabel}
                data-testid="photo-mode-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="large">{labels.photoModeLarge}</SelectItem>
                <SelectItem value="small">{labels.photoModeSmall}</SelectItem>
                <SelectItem value="none">{labels.photoModeNone}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1.5">
            <Label htmlFor="co-sort-select" className="whitespace-nowrap text-xs">
              {labels.sortLabel}
            </Label>
            <Select value={sortKey} onValueChange={(v) => onSortKey(v as ClientOfferReviewSortKey)}>
              <SelectTrigger
                id="co-sort-select"
                className="h-9 w-32 rounded-lg text-xs"
                aria-label={labels.sortAriaLabel}
                title={labels.sortAriaLabel}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="city">{labels.sortByCity}</SelectItem>
                <SelectItem value="price">{labels.sortByPrice}</SelectItem>
                <SelectItem value="decision">{labels.sortByDecision}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1.5">
            <Label htmlFor="co-granularity-select" className="whitespace-nowrap text-xs">
              {labels.granularityLabel}
            </Label>
            <Select
              value={granularity}
              onValueChange={(v) => onGranularity(v as ClientOfferReviewGranularity)}
            >
              <SelectTrigger
                id="co-granularity-select"
                className="h-9 w-44 rounded-lg text-xs"
                aria-label={labels.granularityAriaLabel}
                title={labels.granularityAriaLabel}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="HALF_MONTH">{labels.granularityHalfMonth}</SelectItem>
                <SelectItem value="MONTH">{labels.granularityMonth}</SelectItem>
                <SelectItem value="WEEK">{labels.granularityWeek}</SelectItem>
                <SelectItem value="DAY">{labels.granularityDay}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <ClientOfferReviewPageSizeControl
            pageSize={pageSize}
            onPageSize={onPageSize}
            labels={labels}
          />

          <DisabledHintTooltip disabled={readOnly} hint={roHint}>
            <Button
              type="button"
              variant="outline"
              onClick={onSelectAll}
              disabled={readOnly}
              aria-label={labels.selectAllAriaLabel}
              title={readOnly ? roHint : labels.selectAllHint}
              data-testid="select-all-button"
              size="icon-md"
              className="rounded-lg border-emerald-300 text-emerald-700"
            >
              <CheckCheck className="h-4 w-4" strokeWidth={2} />
            </Button>
          </DisabledHintTooltip>
          <DisabledHintTooltip disabled={readOnly} hint={roHint}>
            <Button
              type="button"
              variant="outline"
              onClick={onClearAll}
              disabled={readOnly}
              aria-label={labels.clearAllAriaLabel}
              title={readOnly ? roHint : labels.clearAllHint}
              data-testid="clear-all-button"
              size="icon-md"
              className="rounded-lg border-border text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={2} />
            </Button>
          </DisabledHintTooltip>
          <span
            className="whitespace-nowrap text-xs text-muted-foreground"
            data-testid="offer-review-shown-count"
          >
            {shownLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
